import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Receipt } from "lucide-react";
import { inr } from "./outstandingUtils";

/** Remittance / POP: upload once → AI reads it → confirm → affected invoices pause (awaiting reconciliation, never PAID here). */
export default function RemittanceCapture({ customerName, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const read = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("customer_name", customerName);
    try { const { data } = await api.post("/finance-outstanding/remittance/extract", fd); setR(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read the remittance"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const confirm = async () => {
    const allocations = r.allocations.filter((a) => a.invoice_id && Number(a.amount) > 0).map((a) => ({ invoice_id: a.invoice_id, amount: Number(a.amount) }));
    if (!allocations.length) return toast.error("No open invoice matched in this remittance");
    setBusy(true);
    try { const { data } = await api.post("/finance-outstanding/remittance/confirm", { customer_name: customerName, payment_amount: Number(r.payment_amount), payment_date: r.payment_date, reference: r.reference, allocations });
      toast.success(`Payment proof recorded on ${data.invoices} invoice(s) — chasing paused, awaiting reconciliation`); setR(null); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="os-remittance">
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv" className="hidden" onChange={(e) => read(e.target.files?.[0])} data-testid="os-remittance-input" />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="os-remittance-btn">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Receipt className="h-3.5 w-3.5 mr-1" />}Payment received: upload remittance</Button>
      {r && (
        <div className="rounded border border-violet-200 bg-violet-50/40 p-2.5 space-y-2 text-[12px]" data-testid="os-remittance-review">
          <div className="grid gap-2 sm:grid-cols-3">
            {[["payment_amount", "Amount"], ["payment_date", "Date"], ["reference", "Reference / UTR"]].map(([k, l]) => (
              <label key={k} className="text-[11px] text-slate-500">{l}<Input value={r[k] ?? ""} onChange={(e) => setR({ ...r, [k]: e.target.value })} className="mt-0.5 h-8 text-[12px]" data-testid={`os-remit-${k}`} /></label>))}
          </div>
          <table className="w-full text-[11px]"><thead><tr className="text-left text-slate-500"><th>Invoice</th><th className="text-right">Allocated</th><th className="text-right">Pending</th><th /></tr></thead>
            <tbody>{r.allocations.map((a, i) => <tr key={i} className="border-t border-violet-100"><td className="font-mono">{a.invoice_number}</td>
              <td className="text-right"><Input value={a.amount ?? ""} onChange={(e) => setR({ ...r, allocations: r.allocations.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })} className="h-7 w-28 ml-auto text-right text-[11px]" data-testid={`os-remit-alloc-${i}`} /></td>
              <td className="text-right">{inr(a.pending)}</td><td className="pl-2 text-rose-700">{a.issue}</td></tr>)}</tbody></table>
          <div className="flex gap-2"><Button size="sm" onClick={confirm} disabled={busy} data-testid="os-remittance-confirm">Confirm payment proof</Button><Button size="sm" variant="ghost" onClick={() => setR(null)}>Cancel</Button></div>
        </div>
      )}
    </div>
  );
}
